import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Search, Waves, Eye, Brain, Lightbulb, Shield, Dumbbell,
  MessageSquare, Target, AlertTriangle, BookOpen, CheckCircle
} from 'lucide-react';

const CATEGORIES = [
  { id: 'all', label: 'All' },
  { id: 'sensory', label: 'Sensory' },
  { id: 'communication', label: 'Communication' },
  { id: 'behavior', label: 'Behavior' },
  { id: 'teaching', label: 'Teaching' },
  { id: 'safety', label: 'Safety' },
  { id: 'environment', label: 'Environment' },
  { id: 'equipment', label: 'Equipment' },
];

const KNOWLEDGE_CARDS = [
  {
    id: 'sensory-accommodations',
    category: 'sensory',
    icon: Waves,
    color: 'text-cyan-600',
    bg: 'bg-cyan-50',
    title: 'Sensory Accommodations Checklist',
    source: 'Autism Swim Certification',
    items: [
      { label: 'Water temperature', detail: 'Most neurodivergent children prefer warmer water (86-90\u00b0F / 30-32\u00b0C)' },
      { label: 'Pool acoustics', detail: 'Indoor pools amplify sound \u2014 consider ear defenders or quieter session times' },
      { label: 'Lighting', detail: 'Natural light preferred over fluorescent. Dimmed settings reduce sensory overload' },
      { label: 'Texture sensitivities', detail: 'Pool surfaces, swimwear fabric, goggles, and flotation devices may cause distress' },
      { label: 'Proprioceptive input', detail: 'Water pressure provides natural calming deep-pressure input' },
      { label: 'Vestibular input', detail: 'Floating and spinning can be regulating or overwhelming \u2014 observe responses carefully' },
      { label: 'Crowding', detail: 'Fewer people = less sensory overload. Schedule during quieter pool times' },
    ]
  },
  {
    id: 'visual-supports',
    category: 'teaching',
    icon: Eye,
    color: 'text-purple-600',
    bg: 'bg-purple-50',
    title: 'Visual Supports Guide',
    source: 'Autism Swim Certification',
    items: [
      { label: 'Visual schedules', detail: 'Reduce anxiety by making sessions predictable. Use First-Then boards and task strips' },
      { label: 'Social stories', detail: 'Prepare learners for new experiences with photos of the actual pool environment' },
      { label: 'Visual cue cards', detail: 'Pool rules, safety signals, and skill demonstrations. Laminate for waterproof use' },
      { label: 'Use real photos', detail: 'Real photos over cartoons when possible for better understanding' },
      { label: 'Visual countdown timers', detail: 'Help with transitions between activities. Reduces anxiety about time' },
      { label: 'Consistent routine', detail: 'Arrival \u2192 changing \u2192 warm-up \u2192 skills \u2192 free play \u2192 exit' },
    ]
  },
  {
    id: 'communication-strategies',
    category: 'communication',
    icon: MessageSquare,
    color: 'text-blue-600',
    bg: 'bg-blue-50',
    title: 'Communication Strategies',
    source: 'Alison Autism Awareness',
    items: [
      { label: 'Assess communication style first', detail: 'Verbal, limited verbal, nonverbal, AAC device user, or sign language' },
      { label: 'Clear, concise language', detail: 'One instruction at a time. Avoid idioms and sarcasm' },
      { label: 'Processing time', detail: 'Allow 10-15 seconds before repeating or rephrasing an instruction' },
      { label: 'Use their name first', detail: 'Say the child\'s name before giving an instruction to ensure attention' },
      { label: 'Augment verbal instructions', detail: 'Pair with visual demonstrations, modeling, and hand-over-hand guidance (with consent)' },
      { label: 'Echolalia is functional', detail: 'Repeating words/phrases is communication \u2014 interpret the meaning behind it' },
      { label: 'Total communication approach', detail: 'Combine speech, visuals, gestures, and technology' },
    ]
  },
  {
    id: 'reinforcement-prompting',
    category: 'behavior',
    icon: Target,
    color: 'text-green-600',
    bg: 'bg-green-50',
    title: 'Reinforcement & Prompting',
    source: 'RBT Training (ABA)',
    items: [
      { label: 'Positive reinforcement', detail: 'Add something desirable after behavior to increase it (praise, preferred items/activities)' },
      { label: 'Token economies', detail: 'Earn tokens for completed tasks, exchange for preferred rewards' },
      { label: 'Natural reinforcement', detail: 'The activity itself becomes rewarding (splashing = fun)' },
      { label: 'Prompt hierarchy', detail: 'Full physical \u2192 partial physical \u2192 model \u2192 gestural \u2192 verbal \u2192 independent' },
      { label: 'Least intrusive prompt', detail: 'Use the least amount of help necessary for success' },
      { label: 'Prompt fading', detail: 'Gradually reduce assistance so the learner does more independently' },
      { label: 'Time delay', detail: 'Wait 3-5 seconds before prompting to allow independent responding' },
      { label: 'Reinforcement schedules', detail: 'Start continuous (every time), fade to intermittent as skills develop' },
    ]
  },
  {
    id: 'drowning-prevention',
    category: 'safety',
    icon: Shield,
    color: 'text-red-600',
    bg: 'bg-red-50',
    title: 'Drowning Prevention & Water Safety',
    source: 'Autism Swim Certification',
    items: [
      { label: 'Higher risk population', detail: 'Neurodivergent children are at significantly higher drowning risk due to wandering/elopement' },
      { label: 'Survival skills priority', detail: 'Rolling to float, treading water, reaching for safety, exiting pool independently' },
      { label: 'Layers of protection', detail: 'Barriers (fences, locks), supervision, water competency, emergency planning' },
      { label: 'Touch and go drills', detail: 'Practice what to do if they fall in unexpectedly' },
      { label: 'Teach in natural environments', detail: 'Not just pools \u2014 lakes, bathtubs, open water scenarios' },
      { label: 'Wandering prevention', detail: 'Secure pool areas, visual barriers, consistent supervision, GPS trackers if needed' },
      { label: '"Stop" and "Wait" commands', detail: 'Teach as critical safety commands \u2014 practice regularly' },
    ]
  },
  {
    id: 'session-structure',
    category: 'teaching',
    icon: BookOpen,
    color: 'text-amber-600',
    bg: 'bg-amber-50',
    title: 'Recommended Session Structure',
    source: 'Autism Swim Certification',
    items: [
      { label: '1. Arrival routine', detail: 'Visual schedule review, changing, sensory check-in with the child' },
      { label: '2. Warm-up', detail: 'Gradual water entry, familiar preferred activities to build comfort' },
      { label: '3. Skill work', detail: '2-3 target skills with visual supports and reinforcement. Use task analysis' },
      { label: '4. Free choice / reward', detail: 'Child-led water play \u2014 this is their earned reinforcement time' },
      { label: '5. Cool-down & exit', detail: 'Predictable ending. Preview of next session to reduce transition anxiety' },
    ]
  },
  {
    id: 'equipment-by-need',
    category: 'equipment',
    icon: Dumbbell,
    color: 'text-indigo-600',
    bg: 'bg-indigo-50',
    title: 'Equipment Guide by Need',
    source: 'Autism Swim Certification',
    items: [
      { label: 'Sensory seekers', detail: 'Textured balls, resistance bands, weighted vests, diving toys for input' },
      { label: 'Sensory avoiders', detail: 'Soft foam equipment, introduced gradually. Never force contact' },
      { label: 'Communication support', detail: 'Waterproof visual cue cards, thumbs up/down for comfort checks' },
      { label: 'Motor planning', detail: 'Kickboards, noodles, pull buoys, fins \u2014 introduced one at a time' },
      { label: 'Safety equipment', detail: 'Flotation vests (not water wings), rescue tubes within reach, non-slip mats' },
    ]
  },
  {
    id: 'environmental-checklist',
    category: 'environment',
    icon: CheckCircle,
    color: 'text-teal-600',
    bg: 'bg-teal-50',
    title: 'Environmental Checklist',
    source: 'Autism Swim Certification',
    items: [
      { label: 'Water temperature', detail: '86-90\u00b0F / 30-32\u00b0C preferred for neurodivergent learners' },
      { label: 'Noise level', detail: 'Schedule during quieter pool times if possible' },
      { label: 'Lighting', detail: 'Natural light > fluorescent. Check for flickering lights' },
      { label: 'Crowding level', detail: 'Fewer people = less sensory overload. Private lanes if available' },
      { label: 'Changing facilities', detail: 'Private options available? Sensory-friendly (no hand dryers nearby)?' },
      { label: 'Exit accessibility', detail: 'Can the child leave the water independently if overwhelmed?' },
      { label: 'Pool chemicals', detail: 'Strong chlorine smell can be a sensory trigger. Check ventilation' },
    ]
  },
  {
    id: 'skill-building',
    category: 'teaching',
    icon: Lightbulb,
    color: 'text-yellow-600',
    bg: 'bg-yellow-50',
    title: 'Skill Building Techniques',
    source: 'RBT Training (ABA)',
    items: [
      { label: 'Task analysis', detail: 'Break skills into micro-steps. E.g., "blow bubbles" = approach \u2192 touch face \u2192 blow \u2192 sustain' },
      { label: 'Backward chaining', detail: 'Teach the last step first so the learner experiences success/completion' },
      { label: 'Errorless learning', detail: 'Prompt before errors occur, then systematically fade prompts' },
      { label: 'Shaping', detail: 'Reinforce successive approximations toward a target behavior' },
      { label: 'Generalization', detail: 'Practice skills across different pools, times, and instructors' },
      { label: 'Motor planning time', detail: 'Allow extra processing time. Provide physical demonstrations' },
      { label: 'Whole-body warm-up', detail: 'Before demanding skills to prepare sensory systems' },
    ]
  },
  {
    id: 'behavior-support',
    category: 'behavior',
    icon: Brain,
    color: 'text-rose-600',
    bg: 'bg-rose-50',
    title: 'Behavior Support in Aquatics',
    source: 'Autism Swim Certification',
    items: [
      { label: 'Behavior is communication', detail: 'Before addressing "challenging behavior," ask what the individual is communicating' },
      { label: 'Functions of behavior', detail: 'Escape/avoidance, attention-seeking, access to tangibles, sensory stimulation' },
      { label: 'Antecedent strategies', detail: 'Modify environment before behavior occurs \u2014 reduce noise, provide schedules, offer choices' },
      { label: 'DRA', detail: 'Differential Reinforcement of Alternative behavior \u2014 teach and reinforce a replacement' },
      { label: 'De-escalation', detail: 'Stay calm, reduce demands, offer safe space. Avoid physical confrontation in water' },
      { label: 'Never use water as punishment', detail: 'Never use submersion or withholding safety equipment as a consequence' },
    ]
  },
  {
    id: 'rapport-building',
    category: 'communication',
    icon: Brain,
    color: 'text-pink-600',
    bg: 'bg-pink-50',
    title: 'Building Rapport',
    source: 'Autism Swim Certification',
    items: [
      { label: 'Rapport before teaching', detail: 'Spend initial sessions building trust and comfort in the water environment' },
      { label: 'Follow the learner\'s lead', detail: 'Join their interests, use preferred activities as motivators' },
      { label: 'Consistent instructor', detail: 'Same instructor when possible \u2014 reduces anxiety and builds predictability' },
      { label: 'Gradual exposure', detail: 'Don\'t rush water entry. Some learners need multiple sessions just to be near water' },
      { label: 'Celebrate all progress', detail: 'No matter how small. Avoid comparing to neurotypical benchmarks' },
      { label: 'Parent involvement', detail: 'Parents know their child best \u2014 always incorporate their input' },
    ]
  },
  {
    id: 'co-occurring-conditions',
    category: 'safety',
    icon: AlertTriangle,
    color: 'text-orange-600',
    bg: 'bg-orange-50',
    title: 'Co-occurring Conditions',
    source: 'Autism Swim Certification',
    items: [
      { label: 'Epilepsy', detail: 'Know seizure action plans. Always maintain arm\'s reach supervision in water' },
      { label: 'Hypermobility / low tone', detail: 'Affects swimming posture and endurance \u2014 modify expectations accordingly' },
      { label: 'Anxiety disorders', detail: 'May present as refusal, aggression, or shutdown. Provide predictability and control' },
      { label: 'Sensory Processing Disorder', detail: 'Hyper- or hypo-sensitivity to water temperature, pressure, sounds, lights' },
      { label: 'ADHD', detail: 'Shorter activity durations, frequent transitions, high-interest activities keep engagement' },
      { label: 'GI issues', detail: 'May affect comfort in water. Be aware of bathroom needs and timing' },
    ]
  },
];

export default function KnowledgeLibraryPage() {
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  const filtered = KNOWLEDGE_CARDS.filter(card => {
    const matchesCat = activeCategory === 'all' || card.category === activeCategory;
    const matchesSearch = !search || card.title.toLowerCase().includes(search.toLowerCase()) ||
      card.items.some(item => item.label.toLowerCase().includes(search.toLowerCase()) || item.detail.toLowerCase().includes(search.toLowerCase()));
    return matchesCat && matchesSearch;
  });

  return (
    <div className="max-w-5xl mx-auto animate-fadeIn" data-testid="knowledge-library-page">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-800" style={{ fontFamily: 'Nunito' }}>
          Knowledge Library
        </h1>
        <p className="text-sm text-slate-500 mt-1">Quick-reference cards from professional autism & aquatics courses</p>
        <div className="flex flex-wrap gap-2 mt-2 text-[11px]">
          <a href="https://learn.autism-swim.org/our-courses/neurodiversity-and-aquatics-certification/" target="_blank" rel="noreferrer" className="text-[#0E7490] hover:underline font-medium">Autism Swim Certification</a>
          <span className="text-slate-300">|</span>
          <a href="https://alison.com/course/introduction-to-autism-awareness-and-support" target="_blank" rel="noreferrer" className="text-[#0E7490] hover:underline font-medium">Alison Autism Awareness</a>
          <span className="text-slate-300">|</span>
          <a href="https://autismpartnershipfoundation.org/free-rbt-training/" target="_blank" rel="noreferrer" className="text-[#0E7490] hover:underline font-medium">RBT Training (ABA)</a>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
        <Input
          className="pl-10 rounded-xl"
          placeholder="Search topics, techniques, equipment..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          data-testid="knowledge-search"
        />
      </div>

      {/* Category filters */}
      <div className="flex gap-2 overflow-x-auto pb-3 mb-6">
        {CATEGORIES.map(cat => (
          <button
            key={cat.id}
            onClick={() => setActiveCategory(cat.id)}
            className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${
              activeCategory === cat.id ? 'bg-[#1E1B4B] text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
            }`}
            data-testid={`filter-${cat.id}`}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filtered.map(card => (
          <Card key={card.id} className="rounded-3xl border-slate-100 shadow-sm hover:shadow-md transition-shadow" data-testid={`card-${card.id}`}>
            <CardHeader className="pb-2">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl ${card.bg} flex items-center justify-center shrink-0`}>
                  <card.icon className={`w-5 h-5 ${card.color}`} />
                </div>
                <div>
                  <CardTitle className="text-base text-slate-800">{card.title}</CardTitle>
                  <Badge variant="outline" className="rounded-full text-[10px] mt-0.5 border-slate-200">{card.source}</Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2.5">
                {card.items.map((item, i) => (
                  <div key={`kl-${i}`} className="flex items-start gap-2">
                    <div className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${card.bg.replace('bg-', 'bg-').replace('50', '400')}`} style={{ backgroundColor: `var(--${card.color.replace('text-', '')}, #64748B)` }} />
                    <div>
                      <p className="text-sm font-semibold text-slate-700">{item.label}</p>
                      <p className="text-xs text-slate-500 leading-relaxed">{item.detail}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-16">
          <Search className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="font-bold text-slate-400">No matching topics found</p>
          <p className="text-sm text-slate-400 mt-1">Try a different search term or category</p>
        </div>
      )}
    </div>
  );
}
